/* global BigInt */
// components/Casino/HighCardDuel.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { PublicKey, Transaction } from '@solana/web3.js';
import { 
  getAssociatedTokenAddress, 
  createAssociatedTokenAccountInstruction, 
  createTransferInstruction, 
  TOKEN_PROGRAM_ID,
  getAccount 
} from '@solana/spl-token';
import { TransactionInstruction } from '@solana/web3.js';
import api from '../../api';

// Environment-driven constants
const USDT_MINT = process.env.REACT_APP_USDT_SOL_ADDRESS || 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB'; // Default Solana USDT
const PROJECT_WALLET = process.env.REACT_APP_PROJECT_WALLET_ADDRESS;
const DECIMALS = 6; // USDT on Solana has 6 decimals

// Entry amounts for tables
const TABLE_ENTRIES = [
  { id: 'table_10', amount: 10, label: '$10 Table', popular: true },
  { id: 'table_20', amount: 20, label: '$20 Table', popular: false },
  { id: 'table_50', amount: 50, label: '$50 Table', popular: false },
  { id: 'table_100', amount: 100, label: '$100 Table', popular: false }
];

// Local storage keys for transaction tracking
const LS_KEYS = {
  PROCESSING: 'processingHighCardDuel',
  TX_SIG: 'highCardDuelTxSignature',
  TABLE_ID: 'highCardDuelTableId',
  START_TIME: 'highCardDuelStartTime'
};

const HighCardDuel = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Connection to Solana
  const { connection } = useConnection();
  const { setVisible } = useWalletModal();
  const {
    publicKey,
    sendTransaction,
    connected,
    disconnect,
    wallets,
    select,
    connect
  } = useWallet();
  
  // UI states
  const [view, setView] = useState(id ? 'game' : 'lobby');
  const [isMobile, setIsMobile] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showRules, setShowRules] = useState(false);
  
  // Lobby states
  const [activeTables, setActiveTables] = useState([]);
  const [loadingTable, setLoadingTable] = useState(null);
  const [txSig, setTxSig] = useState('');
  const [recoveryInProgress, setRecoveryInProgress] = useState(false);
  const [waitingTable, setWaitingTable] = useState(null);
  
  // Game states
  const [game, setGame] = useState(null);
  const [loadingGame, setLoadingGame] = useState(true);
  const [isPlayer, setIsPlayer] = useState(false);
  const [pollInterval, setPollInterval] = useState(null);
  const [cardFlipped, setCardFlipped] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Card suit symbols and colors
  const suitSymbols = {
    'Hearts': '♥',
    'Diamonds': '♦',
    'Clubs': '♣',
    'Spades': '♠'
  };
  
  const suitColors = {
    'Hearts': 'text-red-500',
    'Diamonds': 'text-red-500',
    'Clubs': 'text-white',
    'Spades': 'text-white'
  };
  
  // Detect mobile device
  useEffect(() => {
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  }, []);
  
  // Handle view changes based on URL
  useEffect(() => {
    setView(id ? 'game' : 'lobby');
  }, [id, location]);

  // ----- LOBBY FUNCTIONALITY -----
  
  // Load active tables on component mount
  useEffect(() => {
    if (view === 'lobby') {
      fetchActiveTables();
      
      // Set up polling for table updates
      const intervalId = setInterval(fetchActiveTables, 5000);
      
      return () => clearInterval(intervalId);
    }
  }, [view]);
  
  // Check for pending transactions on page load
  useEffect(() => {
    if (view === 'lobby') {
      checkPendingTransactions();
      
      if (connected) {
        checkPendingTransactions();
      }
    }
  }, [connected, view]);
  
  // Trigger confetti effect when game completes and player wins
  useEffect(() => {
    if (game && game.status === 'completed') {
      const userId = localStorage.getItem('userId');
      const result = game.gameData?.result;
      const isWinner = result?.winnerUserId === userId;
      
      if (isWinner) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      }
    }
  }, [game]);
  
  // Fetch active tables from the server
  const fetchActiveTables = async () => {
    try {
      const response = await api.get('/casino/high-card-duel/tables');
      if (response.data.success) {
        setActiveTables(response.data.tables);
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  };
  
  // Check if token account exists
  const accountExists = async (address) => {
    try {
      await getAccount(connection, address);
      return true;
    } catch (error) {
      return false;
    }
  };
  
  // Wait for transaction confirmation with timeout
  const waitForConfirmation = async (connection, signature, timeoutMs = 60_000) => {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      try {
        const resp = await connection.getSignatureStatuses([signature]);
        const info = resp.value[0];
        if (info) {
          if (info.err) {
            throw new Error(`Transaction failed: ${JSON.stringify(info.err)}`);
          }
          if (
            info.confirmationStatus === 'confirmed' ||
            info.confirmationStatus === 'finalized'
          ) {
            return info;
          }
        }
        await new Promise((r) => setTimeout(r, 2000));
      } catch (err) {
        console.error("Error checking confirmation:", err);
        // Continue trying despite errors
      }
    }
    throw new Error(`Timed out after ${timeoutMs / 1000}s waiting for confirmation`);
  };
  
  // Save transaction progress to localStorage
  const saveTransactionProgress = (tableId, txSig = null) => {
    localStorage.setItem(LS_KEYS.PROCESSING, 'true');
    localStorage.setItem(LS_KEYS.TABLE_ID, tableId);
    localStorage.setItem(LS_KEYS.START_TIME, Date.now().toString());
    
    if (txSig) {
      localStorage.setItem(LS_KEYS.TX_SIG, txSig);
    }
  };

  // Clear transaction progress from localStorage
  const clearTransactionProgress = () => {
    localStorage.removeItem(LS_KEYS.PROCESSING);
    localStorage.removeItem(LS_KEYS.TX_SIG);
    localStorage.removeItem(LS_KEYS.TABLE_ID);
    localStorage.removeItem(LS_KEYS.START_TIME);
  };
  
  // Check for pending transactions
  const checkPendingTransactions = async () => {
    if (recoveryInProgress || !connected) return;
    
    const isProcessing = localStorage.getItem(LS_KEYS.PROCESSING) === 'true';
    const txSig = localStorage.getItem(LS_KEYS.TX_SIG);
    const tableId = localStorage.getItem(LS_KEYS.TABLE_ID);
    const startTimeStr = localStorage.getItem(LS_KEYS.START_TIME);
    const startTime = startTimeStr ? parseInt(startTimeStr) : 0;
    
    if (!isProcessing) return;
    
    setRecoveryInProgress(true);
    setError('Checking status of your previous table join transaction...');
    
    try {
      if (txSig) {
        setTxSig(txSig);
        
        const table = TABLE_ENTRIES.find(t => t.id === tableId);
        if (!table) {
          throw new Error(`Unknown table ID: ${tableId}`);
        }
        
        setLoadingTable(tableId);
        
        try {
          // Check transaction status on blockchain
          const status = await connection.getSignatureStatus(txSig);
          console.log('Previous transaction status:', status);
          
          if (!status?.value) {
            throw new Error('Transaction not found on blockchain');
          }
          
          if (status.value.err) {
            setError(`Previous transaction failed: ${JSON.stringify(status.value.err)}`);
            clearTransactionProgress();
            setLoadingTable(null);
            setRecoveryInProgress(false);
            return;
          }
          
          // Transaction is on chain and didn't fail
          if (
            status.value.confirmationStatus === 'confirmed' ||
            status.value.confirmationStatus === 'finalized'
          ) {
            // Transaction confirmed, now notify backend
            try {
              const token = localStorage.getItem('token');
              if (!token) {
                setError('Please log in to complete your table join');
                setLoadingTable(null);
                setRecoveryInProgress(false);
                return;
              }
              
              const response = await api.post('/casino/high-card-duel/join', { 
                signature: txSig, 
                tableId: tableId 
              });
              
              // Successfully joined the table
              if (response.data.table.status === 'waiting') {
                setSuccess(`You've joined the ${table.label}! Waiting for an opponent...`);
                setWaitingTable(response.data.table);
              } else if (response.data.table.status === 'active') {
                setSuccess(`Table joined! Redirecting to game...`);
                setTimeout(() => {
                  navigate(`/casino/high-card-duel/${response.data.table._id}`);
                }, 1500);
              }
              
              clearTransactionProgress();
            } catch (apiErr) {
              // Check if already processed
              if (apiErr.response?.data?.alreadyJoined) {
                setSuccess(`You've already joined this table!`);
                setTimeout(() => {
                  navigate(`/casino/high-card-duel/${apiErr.response.data.tableId}`);
                }, 1500);
                clearTransactionProgress();
              } else {
                setError(`Error joining table: ${apiErr.response?.data?.error || apiErr.message}`);
              }
            }
          } else {
            setError('Your previous transaction is still being confirmed. Please wait...');
          }
        } catch (statusErr) {
          console.error('Error checking transaction status:', statusErr);
          
          const now = Date.now();
          const timeElapsed = now - startTime;
          
          if (timeElapsed > 10 * 60 * 1000) {
            setError(`Unable to verify transaction status. Please contact support with this ID: ${txSig}`);
          } else {
            setError('Still checking transaction status...');
          }
        }
      } else if (tableId) {
        const now = Date.now();
        const timeElapsed = now - startTime;
        
        if (timeElapsed > 5 * 60 * 1000) {
          setError('Previous join attempt timed out. You can try again.');
          clearTransactionProgress();
        } else {
          setError('You have a pending table join. Please check your wallet for approval requests.');
        }
      }
    } catch (err) {
      console.error('Error in recovery process:', err);
      setError(`Recovery process error: ${err.message}`);
    } finally {
      setLoadingTable(null);
      setRecoveryInProgress(false);
    }
  };
  
  // Join a table by creating a transaction and sending USDT to the project wallet
  const joinTable = async (tableEntry) => {
    if (!connected || !publicKey) {
      return alert('Please connect your wallet first.');
    }

    setError('');
    setTxSig('');
    setSuccess('');
    setLoadingTable(tableEntry.id);
    
    // Save the initial transaction state
    saveTransactionProgress(tableEntry.id);
  
    try {
      console.log('Starting table join process for:', tableEntry.id);
      
      const mint = new PublicKey(USDT_MINT);
      const projectPubkey = new PublicKey(PROJECT_WALLET);
      const owner = publicKey;
      
      console.log('User wallet:', owner.toString());
      console.log('Is mobile device:', isMobile);
      
      // Check SOL balance first for transaction fees
      const solBalance = await connection.getBalance(owner);
      console.log('SOL balance:', solBalance / 1e9, 'SOL');
      
      if (solBalance < 5000000) { // 0.005 SOL minimum for fees
        throw new Error('Insufficient SOL balance for transaction fees');
      }
      
      // Get user's token account
      const userAtaAddr = await getAssociatedTokenAddress(mint, owner);
      console.log('User token account:', userAtaAddr.toString());
      
      // Check if user has the USDT tokens before proceeding
      let userAccountExists = false;
      let userTokenBalance = BigInt(0);
      
      try {
        const userAccount = await getAccount(connection, userAtaAddr);
        userAccountExists = true;
        userTokenBalance = userAccount.amount;
        console.log('User USDT balance:', userTokenBalance.toString());
        
        // Check if user has enough tokens
        const requiredAmount = BigInt(Math.round(tableEntry.amount * Math.pow(10, DECIMALS)));
        if (userTokenBalance < requiredAmount) {
          throw new Error(`Insufficient USDT. You need $${tableEntry.amount} USDT but have ${Number(userTokenBalance) / Math.pow(10, DECIMALS)}.`);
        }
      } catch (e) {
        if (e.message.includes('Insufficient')) {
          throw e;
        }
        console.log('User token account does not exist or error:', e.message);
        userAccountExists = false;
      }
      
      // Mobile-specific transaction handling
      if (isMobile) {
        console.log('Using mobile-optimized transaction flow');
        const mobileTimeout = 120000; // 2 minutes
        
        // Get a fresh blockhash with longer validity
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('finalized');
        
        // Create a SINGLE transaction for mobile to avoid multiple app switches
        const mobileTx = new Transaction();
        mobileTx.feePayer = owner;
        mobileTx.recentBlockhash = blockhash;

        // Add memo instruction to identify the transaction
        mobileTx.add(
          new TransactionInstruction({
            keys: [],
            programId: new PublicKey("MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr"),
            data: new TextEncoder().encode(`High Card Duel - Joining table: ${tableEntry.id}`),
          })
        );
        
        // Create token account if needed
        if (!userAccountExists) {
          console.log('Adding instruction to create user token account');
          mobileTx.add(
            createAssociatedTokenAccountInstruction(
              owner,         // payer
              userAtaAddr,   // ATA address
              owner,         // ATA owner
              mint
            )
          );
        }
        
        // Check project token account
        const projectAtaAddr = await getAssociatedTokenAddress(mint, projectPubkey);
        console.log('Project token account:', projectAtaAddr.toString());
        
        let projectAccountExists = false;
        try {
          await getAccount(connection, projectAtaAddr);
          projectAccountExists = true;
          console.log('Project token account exists');
        } catch (e) {
          console.log('Project token account does not exist');
          projectAccountExists = false;
        }
        
        // Create project account if needed
        if (!projectAccountExists) {
          console.log('Adding instruction to create project token account');
          mobileTx.add(
            createAssociatedTokenAccountInstruction(
              owner,           // payer
              projectAtaAddr,  // ATA address
              projectPubkey,   // ATA owner
              mint
            )
          );
        }

        console.log('Table entry amount:', tableEntry.amount);
        console.log('DECIMALS value:', DECIMALS);
        
        // Calculate transfer amount
        const transferAmount = BigInt(Math.round(tableEntry.amount * Math.pow(10, DECIMALS)));
        console.log('Transfer amount (raw):', transferAmount.toString());
        
        // Add transfer instruction
        console.log('Adding transfer instruction for mobile');
        mobileTx.add(
          createTransferInstruction(
            userAtaAddr,      // from
            projectAtaAddr,   // to
            owner,            // owner
            transferAmount,   // amount
            [],               // multisig signers (empty)
            TOKEN_PROGRAM_ID
          )
        );
        
        console.log('Mobile transaction prepared, sending...');
        
        // For mobile, add a longer timeout
        const mobileConfig = {
          skipPreflight: false,
          preflightCommitment: 'processed',
          maxRetries: 5,
        };
        
        try {
          // Send mobile transaction
          const sig = await sendTransaction(mobileTx, connection, mobileConfig);
          console.log('Mobile transaction sent, signature:', sig);
          setTxSig(sig);
          
          // Update localStorage with signature
          saveTransactionProgress(tableEntry.id, sig);
          
          // Wait for confirmation with longer timeout
          console.log('Waiting for mobile transaction confirmation...');
          await waitForConfirmation(connection, sig, mobileTimeout);
          
          // Notify backend about the table join
          console.log('Notifying backend for mobile transaction...');
          const response = await api.post('/casino/high-card-duel/join', { 
            signature: sig, 
            tableId: tableEntry.id 
          });
          
          if (response.data.table.status === 'waiting') {
            setSuccess(`You've joined the ${tableEntry.label}! Waiting for an opponent...`);
            setWaitingTable(response.data.table);
          } else if (response.data.table.status === 'active') {
            setSuccess(`Table joined! Redirecting to game...`);
            setTimeout(() => {
              navigate(`/casino/high-card-duel/${response.data.table._id}`);
            }, 1500);
          }
          
          // Clear localStorage now that we're done
          clearTransactionProgress();
        } catch (err) {
          console.error('Mobile transaction error:', err);
          if (err.message?.includes('User rejected') || err.message?.includes('cancelled')) {
            setError('Transaction was rejected in the wallet app. Please try again.');
            clearTransactionProgress(); // Clear since user explicitly rejected
          } else {
            setError(`Mobile transaction error: ${err.message || 'Unknown error'}`);
            // Don't clear localStorage for other errors so we can recover
          }
        }
      } else {
        // Desktop flow
        console.log('Using desktop transaction flow');
        
        // Get recent blockhash
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('finalized');
        
        // TRANSACTION 1: Create accounts if needed
        if (!userAccountExists) {
          console.log('Creating user token account first...');
          const createAccountTx = new Transaction();
          createAccountTx.feePayer = owner;
          createAccountTx.recentBlockhash = blockhash;
          
          createAccountTx.add(
            createAssociatedTokenAccountInstruction(
              owner,         // payer
              userAtaAddr,   // ATA address
              owner,         // ATA owner
              mint
            )
          );
          
          const createSig = await sendTransaction(createAccountTx, connection);
          console.log('Account creation transaction sent:', createSig);
          
          // Wait for confirmation
          await connection.confirmTransaction({
            signature: createSig,
            blockhash: blockhash,
            lastValidBlockHeight: lastValidBlockHeight
          });
          
          console.log('User token account created successfully');
        }
        
        // Get a fresh blockhash for the transfer transaction
        const freshBlockhashData = await connection.getLatestBlockhash('finalized');
        
        // TRANSACTION 2: Transfer tokens
        const transferTx = new Transaction();
        transferTx.feePayer = owner;
        transferTx.recentBlockhash = freshBlockhashData.blockhash;
        
        // Add memo instruction
        transferTx.add(
          new TransactionInstruction({
            keys: [],
            programId: new PublicKey("MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr"),
            data: new TextEncoder().encode(`High Card Duel - Joining table: ${tableEntry.id}`),
          })
        );
        
        // Check project token account
        const projectAtaAddr = await getAssociatedTokenAddress(mint, projectPubkey);
        console.log('Project token account:', projectAtaAddr.toString());
        
        let projectAccountExists = false;
        try {
          await getAccount(connection, projectAtaAddr);
          projectAccountExists = true;
          console.log('Project token account exists');
        } catch (e) {
          console.log('Project token account does not exist');
          projectAccountExists = false;
        }
        
        // Create project account if needed
        if (!projectAccountExists) {
          console.log('Adding instruction to create project token account');
          transferTx.add(
            createAssociatedTokenAccountInstruction(
              owner,           // payer
              projectAtaAddr,  // ATA address
              projectPubkey,   // ATA owner
              mint
            )
          );
        }

        console.log('Table entry amount:', tableEntry.amount);
        console.log('DECIMALS value:', DECIMALS);
        
        // Calculate transfer amount - ensure it's a valid integer
        const transferAmount = BigInt(Math.round(tableEntry.amount * Math.pow(10, DECIMALS)));
        console.log('Transfer amount (raw):', transferAmount.toString());
        
        // Add transfer instruction
        console.log('Adding transfer instruction');
        transferTx.add(
          createTransferInstruction(
            userAtaAddr,      // from
            projectAtaAddr,   // to
            owner,            // owner
            transferAmount,   // amount
            [],               // multisig signers (empty)
            TOKEN_PROGRAM_ID
          )
        );
        
        console.log('Transfer transaction prepared, sending...');
        
        try {
          // Send transfer transaction
          const sig = await sendTransaction(transferTx, connection);
          console.log('Transfer transaction sent, signature:', sig);
          
          setTxSig(sig);
          
          // Update localStorage with signature
          saveTransactionProgress(tableEntry.id, sig);
          
          // Wait for confirmation
          await waitForConfirmation(connection, sig, 60_000);
          
          // Notify backend
          console.log('Notifying backend about table join...');
          const response = await api.post('/casino/high-card-duel/join', { 
            signature: sig, 
            tableId: tableEntry.id 
          });
          
          if (response.data.table.status === 'waiting') {
            setSuccess(`You've joined the ${tableEntry.label}! Waiting for an opponent...`);
            setWaitingTable(response.data.table);
          } else if (response.data.table.status === 'active') {
            setSuccess(`Table joined! Redirecting to game...`);
            setTimeout(() => {
              navigate(`/casino/high-card-duel/${response.data.table._id}`);
            }, 1500);
          }
          
          // Clear localStorage after successful join
          clearTransactionProgress();
        } catch (err) {
          console.error('Transaction Error:', err);
          
          if (err.message?.includes('User rejected')) {
            setError('Transaction was rejected in your wallet. Please try again.');
            clearTransactionProgress();
          } else if (err.message?.includes('timeout') || err.message?.includes('Timed out')) {
            setError('Connection to wallet timed out. Please check your wallet app is open and try again.');
          } else if (err.message?.includes('insufficient funds')) {
            setError('Insufficient funds in your wallet for this transaction.');
            clearTransactionProgress();
          } else {
            setError(err.response?.data?.error || err.message || 'An unexpected error occurred.');
          }
        }
      }
      
    } catch (err) {
      console.error('Pre-transaction Error:', err);
      
      if (err.message?.includes('Insufficient USDT')) {
        setError(err.message);
      } else if (err.message?.includes('Insufficient SOL balance')) {
        setError('Insufficient SOL balance for transaction fees. You need at least 0.005 SOL.');
      } else {
        setError(err.response?.data?.error || err.message || 'An unexpected error occurred.');
      }
      
      clearTransactionProgress();
    } finally {
      setLoadingTable(null);
    }
  };

  // ----- GAME FUNCTIONALITY -----
  
  // Flip player's card
  const flipCard = () => {
    setCardFlipped(!cardFlipped);
  };
  
  // Fetch game data
  const fetchGame = async () => {
    if (!id) return;
    
    try {
      setLoadingGame(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        return navigate('/login');
      }
      
      const response = await api.get(`/casino/high-card-duel/game/${id}`);
      
      if (response.data.success) {
        setGame(response.data.game);
        setIsPlayer(response.data.isPlayer);
        
        // If game is complete, stop polling
        if (response.data.game.status === 'completed') {
          if (pollInterval) {
            clearInterval(pollInterval);
            setPollInterval(null);
          }
        }
      } else {
        setError(response.data.error || 'Failed to load game');
      }
    } catch (error) {
      console.error('Error fetching game:', error);
      setError(error.response?.data?.error || 'Error loading game');
    } finally {
      setLoadingGame(false);
    }
  };
  
  // Set up polling for game updates
  useEffect(() => {
    if (view === 'game' && id) {
      fetchGame(); // Initial fetch
      
      // Set up interval for polling
      const interval = setInterval(fetchGame, 3000);
      setPollInterval(interval);
      
      // Clean up on unmount
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }
  }, [id, view]);
  
  // Render card component
  const renderCard = (card, isHidden = false, isFlippable = false) => {
    if (isHidden) {
      return (
        <div 
          className={`relative w-32 h-44 sm:w-36 sm:h-52 md:w-40 md:h-56 bg-gray-800 rounded-xl border-2 border-gray-700 flex items-center justify-center shadow-lg 
                    ${isFlippable ? 'cursor-pointer transform transition-all duration-300 hover:scale-105' : ''}
                    ${cardFlipped && isFlippable ? 'rotate-y-180' : ''}`}
          onClick={isFlippable ? flipCard : undefined}
        >
          <div className="absolute inset-0 w-full h-full rounded-lg flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
            <div className="w-2/3 h-2/3 rounded-lg bg-gradient-to-br from-indigo-900/40 to-violet-800/40 flex items-center justify-center border border-violet-700/40">
              <svg className="w-12 h-12 text-violet-500/80" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          
          {isFlippable && (
            <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-pink-500 to-violet-500 text-white text-xs font-medium py-1 px-3 rounded-full animate-pulse">
              Tap to view
            </div>
          )}
        </div>
      );
    }
    
    const suitColor = suitColors[card.suit] || 'text-white';
    
    return (
      <div 
        className={`relative w-32 h-44 sm:w-36 sm:h-52 md:w-40 md:h-56 bg-white rounded-xl border-2 border-gray-300 flex flex-col items-center justify-center shadow-lg 
                  transform transition-all duration-300 hover:scale-105 ${isFlippable ? 'cursor-pointer' : ''}`}
        onClick={isFlippable ? flipCard : undefined}
      >
        <div className="absolute top-2 left-2 flex flex-col items-center">
          <div className="text-xl font-bold text-black">{card.value}</div>
          <div className={`text-2xl ${suitColor}`}>{suitSymbols[card.suit]}</div>
        </div>
        
        <div className={`text-6xl ${suitColor}`}>{suitSymbols[card.suit]}</div>
        
        <div className="absolute bottom-2 right-2 flex flex-col items-center rotate-180">
          <div className="text-xl font-bold text-black">{card.value}</div>
          <div className={`text-2xl ${suitColor}`}>{suitSymbols[card.suit]}</div>
        </div>
        
        {/* Card shine effect */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/10 to-transparent pointer-events-none"></div>
      </div>
    );
    };
    
    // Confetti component for winning animation
    const Confetti = () => {
      return (
        <div className="fixed inset-0 pointer-events-none z-50">
          {Array.from({ length: 50 }).map((_, i) => {
            const size = Math.floor(Math.random() * 10) + 5;
            const left = Math.floor(Math.random() * 100);
            const animationDuration = Math.floor(Math.random() * 3) + 2;
            const delay = Math.random() * 2;
            const color = ['bg-pink-500', 'bg-violet-500', 'bg-indigo-500', 'bg-blue-500', 'bg-yellow-500', 'bg-red-500', 'bg-green-500'][Math.floor(Math.random() * 7)];
            
            return (
              <div 
                key={i}
                className={`absolute top-0 ${color} rounded-md opacity-70`}
                style={{
                  width: `${size}px`,
                  height: `${size}px`,
                  left: `${left}%`,
                  animation: `confetti ${animationDuration}s ease-in-out ${delay}s forwards`
                }}
              ></div>
            );
          })}
          
          <style jsx>{`
            @keyframes confetti {
              0% {
                transform: translateY(-10px) rotate(0deg);
                opacity: 1;
              }
              100% {
                transform: translateY(100vh) rotate(720deg);
                opacity: 0;
              }
            }
          `}</style>
        </div>
      );
    };
    
    // ----- RENDER GAME VIEW -----
    const renderGameView = () => {
      if (loadingGame) {
        return (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-pink-500 mb-4"></div>
            <p className="text-gray-300">Loading game data...</p>
          </div>
        );
      }
      
      if (!game) {
        return (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="bg-red-500/20 text-red-300 p-4 rounded-lg max-w-md text-center mb-6">
              <p className="font-medium">Game not found or has been removed.</p>
            </div>
            <button
              onClick={() => navigate('/casino/high-card-duel')}
              className="px-5 py-2.5 bg-gradient-to-r from-violet-500 to-pink-500 rounded-lg font-medium shadow-lg shadow-pink-500/20 hover:shadow-pink-500/40 transition-all duration-300"
            >
              Return to Lobby
            </button>
          </div>
        );
      }
      
      const isCompleted = game.status === 'completed';
      const result = game.gameData?.result;
      const userId = localStorage.getItem('userId');
      const isWinner = result?.winnerUserId === userId;
      const isTie = result?.isTie;
      
      // Find player's position (whether it's player 1 or 2)
      const playerIndex = game.players.findIndex(p => p.userId === userId);
      const opponentIndex = playerIndex === 0 ? 1 : 0;
      
      // Get player cards if available
      const playerCard = game.gameData?.cards?.find(c => c.userId === userId);
      const opponentCard = game.gameData?.cards?.find(c => c.userId !== userId);
      
      // Check if waiting for opponent to view card
      const waitingForOpponent = isPlayer && 
        game.status === 'active' && 
        game.viewedCards?.length === 1 && 
        game.viewedCards.includes(userId);
        
      return (
        <div className="max-w-6xl mx-auto pt-4 pb-12 px-4">
          {/* Winning Confetti */}
          {showConfetti && <Confetti />}
          
          {/* Game Header */}
          <div className="text-center mb-6">
            <h1 className="text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 mb-2">
              High Card Duel
            </h1>
            
            <div className="flex items-center justify-center">
              <span className="text-sm text-gray-400 mr-2">Game ID:</span>
              <span className="text-sm font-mono">{game._id}</span>
            </div>
            
            <div className="mt-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                ${game.status === 'waiting' ? 'bg-yellow-500/20 text-yellow-300' : 
                  game.status === 'active' ? 'bg-green-500/20 text-green-300' : 
                  game.status === 'completed' ? 'bg-blue-500/20 text-blue-300' : 'bg-gray-500/20 text-gray-300'
                }`}
              >
                {game.status === 'waiting' ? 'Waiting for Players' : 
                 game.status === 'active' ? 'Game in Progress' : 
                 game.status === 'completed' ? 'Game Completed' : game.status}
              </span>
              
              <span className="ml-2 bg-violet-500/20 text-violet-300 text-xs px-2 py-0.5 rounded">
                ${game.entryAmount} Table
              </span>
            </div>
          </div>
          
          {/* Game Board */}
          <div className="relative bg-gradient-to-br from-gray-900/70 to-gray-800/70 backdrop-blur-sm rounded-xl border border-gray-700 p-4 sm:p-6 mb-6 shadow-xl shadow-violet-500/5">
            {/* Status Messages at the top */}
            {isCompleted && (
              <div className={`absolute top-0 left-0 right-0 text-center py-2 px-4 rounded-t-xl font-bold text-lg z-10
                ${isTie ? 'bg-blue-500/30 text-blue-200' : 
                  isWinner ? 'bg-green-500/30 text-green-200' : 
                  'bg-red-500/30 text-red-200'}`}
              >
                {isTie ? 'It\'s a Tie! Entry returned' : 
                 isWinner ? `You Win $${Number(result.winningAmount).toFixed(2)}!` : 
                 'You Lost'}
              </div>
            )}
            
            {waitingForOpponent && (
              <div className="absolute top-0 left-0 right-0 bg-yellow-500/30 text-yellow-200 text-center py-2 px-4 rounded-t-xl font-bold z-10">
                Waiting for opponent to view their card...
              </div>
            )}
            
            {/* Game Table */}
            <div className="flex flex-col items-center justify-center py-8 sm:py-12">
              {/* Opponent Section */}
              <div className="flex flex-col items-center mb-8 relative">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center mb-2 border-2 border-gray-700 relative shadow-lg shadow-violet-500/5">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  
                  {game.players[opponentIndex] && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full border border-gray-800 animate-pulse"></div>
                  )}
                </div>
                
                <p className="text-base font-medium mb-4">
                  {game.players[opponentIndex]?.username || 'Waiting for opponent...'}
                </p>
                
                <div className="relative">
                  {opponentCard ? 
                    renderCard(opponentCard) : 
                    (game.status !== 'waiting' ? renderCard(null, true) : (
                      <div className="w-32 h-44 sm:w-36 sm:h-52 md:w-40 md:h-56 bg-gray-800/40 rounded-xl border-2 border-dashed border-gray-700 flex items-center justify-center">
                        <svg className="w-12 h-12 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    ))
                  }
                </div>
              </div>
              
              {/* Center Table */}
              <div className="w-48 h-20 bg-gradient-to-r from-violet-900/50 to-pink-900/50 rounded-full border border-violet-700/30 flex items-center justify-center mb-8 shadow-lg shadow-pink-500/10">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-300 mb-1">Pot</p>
                  <p className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-300 to-pink-300">${game.pot.toFixed(2)}</p>
                </div>
              </div>
              
              {/* Player Section */}
              <div className="flex flex-col items-center relative">
                <div className="relative mb-4">
                  {playerCard ? 
                    renderCard(playerCard) : 
                    (isPlayer && game.status !== 'waiting' ? renderCard(null, true, true) : (
                      <div className="w-32 h-44 sm:w-36 sm:h-52 md:w-40 md:h-56 bg-gray-800/40 rounded-xl border-2 border-dashed border-gray-700 flex items-center justify-center">
                        <svg className="w-12 h-12 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    ))
                  }
                </div>
                
                <div className="w-16 h-16 bg-gradient-to-r from-violet-500 to-pink-500 rounded-full flex items-center justify-center mb-1 relative shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                
                <p className="text-base font-bold">
                  {isPlayer ? 'You' : game.players[playerIndex]?.username || 'Player 1'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Game Info and Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Game Details */}
            <div className="bg-gradient-to-br from-gray-900/70 to-gray-800/70 rounded-xl border border-gray-700 p-4 shadow-lg shadow-violet-500/5">
              <h3 className="text-lg font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">Game Details</h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Entry Fee:</span>
                  <span className="font-medium">${game.entryAmount.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Status:</span>
                  <span className={`font-medium px-2 py-0.5 rounded-full text-xs
                    ${game.status === 'waiting' ? 'bg-yellow-500/20 text-yellow-300' : 
                     game.status === 'active' ? 'bg-green-500/20 text-green-300' : 
                     game.status === 'completed' ? 'bg-blue-500/20 text-blue-300' : ''}`}
                  >
                    {game.status === 'waiting' ? 'Waiting for Players' : 
                     game.status === 'active' ? 'Game in Progress' : 
                     game.status === 'completed' ? 'Game Completed' : game.status}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Players:</span>
                  <span className="font-medium">{game.players.length}/2</span>
                </div>
                
                {isCompleted && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Result:</span>
                      <span className={`font-medium px-2 py-0.5 rounded-full text-xs
                        ${isTie ? 'bg-blue-500/20 text-blue-300' : 
                          isWinner ? 'bg-green-500/20 text-green-300' : 
                          'bg-red-500/20 text-red-300'}`}
                      >
                        {isTie ? 'Tie' : `Player ${result.winnerUserId === game.players[0].userId ? '1' : '2'} Wins`}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Winnings:</span>
                      <span className="font-medium text-green-400">${Number(result.winningAmount).toFixed(2)}</span>
                    </div>
                  </>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Created:</span>
                  <span className="font-medium">{new Date(game.createdAt).toLocaleString()}</span>
                </div>
              </div>
            </div>
            
            {/* Game Rules */}
            <div className="bg-gradient-to-br from-gray-900/70 to-gray-800/70 rounded-xl border border-gray-700 p-4 shadow-lg shadow-violet-500/5">
              <h3 className="text-lg font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">How to Play</h3>
              
              <div className="space-y-2 text-sm">
                <p className="flex items-start">
                  <span className="inline-block w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 mr-2 flex-shrink-0 flex items-center justify-center text-xs">1</span>
                  Each player pays an entry fee to join the table.
                </p>
                <p className="flex items-start">
                  <span className="inline-block w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 mr-2 flex-shrink-0 flex items-center justify-center text-xs">2</span>
                  Once two players join, each player is dealt one card.
                </p>
                <p className="flex items-start">
                  <span className="inline-block w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 mr-2 flex-shrink-0 flex items-center justify-center text-xs">3</span>
                  The player with the higher card wins 95% of the pot.
                </p>
                <p className="flex items-start">
                  <span className="inline-block w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 mr-2 flex-shrink-0 flex items-center justify-center text-xs">4</span>
                  In case of a tie, both players get their entry fees back.
                </p>
                <p className="flex items-start">
                  <span className="inline-block w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 mr-2 flex-shrink-0 flex items-center justify-center text-xs">5</span>
                  Card ranks: 2 (lowest) through Ace (highest).
                </p>
              </div>
            </div>
            
            {/* Actions */}
            <div className="bg-gradient-to-br from-gray-900/70 to-gray-800/70 rounded-xl border border-gray-700 p-4 flex flex-col shadow-lg shadow-violet-500/5">
              <h3 className="text-lg font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">Actions</h3>
              
              <div className="flex-grow space-y-3">
                {game.status === 'completed' && (
                  <div className={`p-3 rounded-lg text-center font-medium
                    ${isTie ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 
                      isWinner ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 
                      'bg-red-500/20 text-red-300 border border-red-500/30'}`}
                  >
                    {isTie ? 'Game ended in a tie! Entry fee returned.' : 
                     isWinner ? `Congratulations! You won $${Number(result.winningAmount).toFixed(2)}` : 
                     'Better luck next time!'}
                  </div>
                )}
                
                {game.status === 'waiting' && isPlayer && (
                  <div className="p-3 bg-yellow-500/20 text-yellow-300 rounded-lg text-center border border-yellow-500/30">
                    Waiting for another player to join...
                  </div>
                )}
                
                {waitingForOpponent && (
                  <div className="p-3 bg-yellow-500/20 text-yellow-300 rounded-lg text-center border border-yellow-500/30">
                    Your opponent is still viewing their card...
                  </div>
                )}
                
                <button
                  onClick={() => navigate('/casino/high-card-duel')}
                  className="w-full px-4 py-3 bg-gradient-to-r from-violet-600/90 to-pink-600/90 hover:from-violet-600 hover:to-pink-600 rounded-lg font-medium text-sm transition-colors shadow-lg shadow-pink-500/10 hover:shadow-pink-500/20"
                >
                  Return to Lobby
                </button>
                
                {isCompleted && (
                  <button
                    onClick={() => navigate('/casino/high-card-duel')}
                    className="w-full px-4 py-3 bg-gradient-to-r from-green-600/90 to-teal-600/90 hover:from-green-600 hover:to-teal-600 rounded-lg font-medium text-sm transition-colors shadow-lg shadow-green-500/10 hover:shadow-green-500/20"
                  >
                    Play Again
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    };
    
    // ----- RENDER LOBBY VIEW -----
    
    const renderLobbyView = () => {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black -mt-16 pt-4 pb-12 px-4 text-white font-mono">
          {/* Background Casino Elements */}
          <div className="fixed inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full bg-pink-500/5 blur-3xl"></div>
            <div className="absolute top-1/3 right-1/4 w-64 h-64 rounded-full bg-violet-500/5 blur-3xl"></div>
            <div className="absolute bottom-1/4 left-1/3 w-48 h-48 rounded-full bg-blue-500/5 blur-3xl"></div>
            
            <div className="absolute top-0 left-0 w-full h-16 bg-gradient-to-b from-black to-transparent"></div>
            <div className="absolute bottom-0 left-0 w-full h-16 bg-gradient-to-t from-black to-transparent"></div>
          </div>
          
          {/* Header Section */}
          <div className="text-center mb-8 mt-8 sm:mt-12 relative">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 text-center mb-4 drop-shadow-lg">
              High Card Duel
            </h1>
            <div className="max-w-md mx-auto flex items-center justify-center">
              <div className="w-16 h-1 bg-gradient-to-r from-violet-500/50 to-transparent rounded-full"></div>
              <p className="text-gray-300 text-lg max-w-md mx-auto px-4">
                Draw your card, bet your chips, win big!
              </p>
              <div className="w-16 h-1 bg-gradient-to-l from-violet-500/50 to-transparent rounded-full"></div>
            </div>
          </div>
          
          {/* Error and Success Messages */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 text-red-300 p-4 rounded-lg max-w-xl mx-auto mb-6 animate-fadeIn">
              <p>{error}</p>
            </div>
          )}
          
          {success && (
            <div className="bg-green-500/20 border border-green-500/30 text-green-300 p-4 rounded-lg max-w-xl mx-auto mb-6 animate-fadeIn">
              <p>{success}</p>
            </div>
          )}
          
          {/* Custom Wallet Connection Section */}
          <div className="mb-10">
            {!connected ? (
              <div className="relative rounded-xl border border-gray-700 bg-gradient-to-br from-gray-900/70 to-gray-800/70 backdrop-blur-sm p-6 max-w-md mx-auto hover:shadow-xl hover:shadow-pink-500/10 transition-all duration-300">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-violet-500 to-pink-500 text-white text-xs font-bold py-1 px-3 rounded-full shadow-lg shadow-pink-500/20">
                  START HERE
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center mb-4 border border-pink-500/30 shadow-lg shadow-pink-500/10">
                    <svg className="w-10 h-10 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">Connect Wallet to Play</h3>
                  <p className="text-gray-300 mb-4 text-center text-sm">Place your bets and join the action</p>
                  
                  <div className="bg-gray-800/60 rounded-lg p-3 mb-4 w-full border border-gray-700/50">
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center">
                        <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mr-2">
                          <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span>Secure Encrypted Connection</span>
                      </li>
                      <li className="flex items-center">
                        <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mr-2">
                          <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span>Lightning Fast Transactions</span>
                      </li>
                      <li className="flex items-center">
                        <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mr-2">
                          <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span>Instant Cashouts</span>
                      </li>
                    </ul>
                  </div>
                  
                  <button
                  onClick={async () => {
                // 1) ensure user is logged in
                const token = localStorage.getItem('token');
                if (!token) return navigate('/login');

                // 2) on mobile, deep-link via Mobile Wallet Adapter
                if (isMobile) {
                    const mobileAdapter = wallets.find(w => w.name === 'Mobile Wallet Adapter');
                    if (mobileAdapter) {
                    select(mobileAdapter.name);
                    await connect();
                    } else {
                    setVisible(true); // fallback
                    }
                } else {
                    // desktop: show normal modal
                    setVisible(true);
                }
                }}
                className="w-full px-4 py-3 rounded-lg font-bold text-sm bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 transition-all duration-300 flex items-center justify-center shadow-lg shadow-pink-500/20 hover:shadow-pink-500/30"
                >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Connect Wallet
                </button>
              </div>
            </div>
          ) : (
            <div className="relative rounded-xl border border-gray-700 bg-gradient-to-br from-gray-900/70 to-gray-800/70 backdrop-blur-sm p-4 max-w-md mx-auto hover:shadow-xl hover:shadow-pink-500/10 transition-all duration-300">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-green-500 to-teal-500 text-white text-xs font-bold py-1 px-3 rounded-full shadow-lg shadow-green-500/20">
                WALLET CONNECTED
              </div>
              
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-between w-full mb-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-900 to-green-700 flex items-center justify-center mr-3 border border-green-500/50 shadow-lg shadow-green-500/10">
                      <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3 className="text-base font-bold text-green-400">Ready to Play</h3>
                      <p className="text-gray-300 text-xs truncate max-w-xs">
                        {publicKey.toString().slice(0, 6)}...{publicKey.toString().slice(-4)}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={disconnect}
                    className="px-3 py-1 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30 text-xs font-medium transition-colors border border-red-500/30"
                  >
                    Disconnect
                  </button>
                </div>
                
                <div className="flex items-center justify-between w-full bg-gray-800/40 rounded-lg px-3 py-2 mb-3 border border-gray-700/50">
                  <span className="text-sm text-gray-300">Status:</span>
                  <span className="text-sm text-green-400 flex items-center">
                    <svg className="w-3 h-3 mr-1 animate-pulse" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                    Ready to Join Tables
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tables Section */}
        {connected && (
          <div className="tables-section mb-12">
            <h2 className="text-2xl font-bold mb-6 text-center relative">
              <span className="inline-block relative z-10 px-4 text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">Available Tables</span>
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-1 bg-gradient-to-r from-transparent via-violet-500/30 to-transparent rounded-full z-0"></div>
            </h2>
            
            {waitingTable ? (
              <div className="max-w-md mx-auto bg-gradient-to-r from-green-900/50 to-blue-900/50 rounded-xl p-6 border border-green-500/30 text-center mb-8 shadow-lg shadow-green-500/10">
                <div className="relative animate-pulse inline-block w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="absolute inset-0 rounded-full border-2 border-green-500/30 animate-spin"></div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-teal-400">Waiting for Opponent</h3>
                <p className="text-gray-300 mb-4">
                  You've joined a {TABLE_ENTRIES.find(t => t.id === waitingTable.entryId)?.label || 'table'}. 
                  Once another player joins, the game will begin automatically.
                </p>
                <div className="text-sm text-gray-400">
                  Table ID: {waitingTable._id}
                </div>
              </div>
            ) : (
<div className="mb-8">
  <div className="relative overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-violet-500/30 scrollbar-track-gray-800/30">
    <div className="flex space-x-4 h-24" style={{ height: '2cm' }}>
      {TABLE_ENTRIES.map((tableEntry) => (
        <div
          key={tableEntry.id}
          className={`relative min-w-[200px] flex-none rounded-xl border ${tableEntry.popular ? 'border-pink-500/50' : 'border-gray-700'} bg-gradient-to-br from-gray-900/70 to-gray-800/70 backdrop-blur-sm p-3 hover:shadow-xl ${tableEntry.popular ? 'hover:shadow-pink-500/20' : 'hover:shadow-violet-500/10'} transition-all transform hover:-translate-y-1 duration-300 flex flex-col justify-between`}
        >
          {tableEntry.popular && (
            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-violet-500 to-pink-500 text-white text-xs font-bold py-0.5 px-2 rounded-full shadow-lg shadow-pink-500/20">
              HOT
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-300 to-pink-300">{tableEntry.label}</h2>
            <span className="flex items-center text-base font-bold">
              <span>${tableEntry.amount.toFixed(2)}</span>
              <span className="ml-1 text-pink-400 text-xs">USDT</span>
            </span>
          </div>
          
          <button
            onClick={() => joinTable(tableEntry)}
            disabled={loadingTable === tableEntry.id || recoveryInProgress}
            className={`w-full px-2 py-1 mt-2 rounded-lg font-bold text-xs transition-all duration-300 ${
              tableEntry.popular
                ? 'bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-600 hover:to-pink-600 shadow-sm shadow-pink-500/20 hover:shadow-pink-500/30'
                : 'bg-gradient-to-r from-violet-500/70 to-pink-500/70 hover:from-violet-500 hover:to-pink-500 shadow-sm shadow-violet-500/10 hover:shadow-violet-500/20'
            } disabled:opacity-50 flex items-center justify-center`}
          >
            {loadingTable === tableEntry.id ? (
              <>
                <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : recoveryInProgress ? (
              <>
                <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Checking...
              </>
            ) : (
              <>
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                Join Table
              </>
            )}
          </button>
        </div>
      ))}
    </div>
  </div>
</div>
            )}
            
            {/* Active Tables Section */}
            <div className="mb-8">
              <h3 className="text-xl font-bold mb-4 relative">
                <span className="inline-block relative z-10 px-4 text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">Tables in Progress</span>
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-px bg-gradient-to-r from-transparent via-violet-500/30 to-transparent rounded-full z-0"></div>
              </h3>
              
              {activeTables.length > 0 ? (
                <div className="bg-gradient-to-br from-gray-900/70 to-gray-800/70 border border-gray-700 rounded-xl overflow-hidden shadow-lg shadow-violet-500/5">
                  <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-violet-500/30 scrollbar-track-gray-800/30">
                    <table className="w-full text-left">
                      <thead className="bg-gradient-to-r from-gray-800/80 to-gray-900/80">
                        <tr>
                          <th className="px-4 py-3 text-sm font-medium text-gray-300">Table ID</th>
                          <th className="px-4 py-3 text-sm font-medium text-gray-300">Entry</th>
                          <th className="px-4 py-3 text-sm font-medium text-gray-300">Players</th>
                          <th className="px-4 py-3 text-sm font-medium text-gray-300">Status</th>
                          <th className="px-4 py-3 text-sm font-medium text-gray-300">Created</th>
                          <th className="px-4 py-3 text-sm font-medium text-gray-300">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800/50">
                        {activeTables.map((table) => {
                          const tableEntry = TABLE_ENTRIES.find(t => t.id === table.entryId);
                          return (
                            <tr key={table._id} className="hover:bg-gray-800/20 transition-colors">
                              <td className="px-4 py-3 text-sm font-mono">{table._id.substring(0, 8)}...</td>
                              <td className="px-4 py-3 text-sm">${tableEntry?.amount || '-'}</td>
                              <td className="px-4 py-3 text-sm">
                                {table.players.length}/2
                                {table.players.some(p => p.walletAddress === publicKey?.toString()) && (
                                  <span className="ml-2 bg-blue-500/20 text-blue-300 text-xs px-2 py-0.5 rounded-full border border-blue-500/30">You</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                                  ${table.status === 'waiting' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' : 
                                    table.status === 'active' ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 
                                    table.status === 'completed' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                                  }`}
                                >
                                  {table.status === 'waiting' ? 'Waiting' : 
                                   table.status === 'active' ? 'In Progress' : 
                                   table.status === 'completed' ? 'Completed' : table.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {new Date(table.createdAt).toLocaleTimeString()}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {table.players.some(p => p.walletAddress === publicKey?.toString()) && table.status === 'active' ? (
                                  <button
                                    onClick={() => navigate(`/casino/high-card-duel/${table._id}`)}
                                    className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white text-xs px-3 py-1 rounded-full transition-colors shadow-md shadow-green-500/20"
                                  >
                                    Play
                                  </button>
                                ) : table.status === 'waiting' && table.players.length < 2 ? (
                                  <button
                                    onClick={() => joinTable(tableEntry)}
                                    disabled={loadingTable || recoveryInProgress}
                                    className="bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white text-xs px-3 py-1 rounded-full transition-colors shadow-md shadow-violet-500/20 disabled:opacity-50"
                                  >
                                    Join
                                  </button>
                                ) : table.status === 'completed' ? (
                                  <button
                                    onClick={() => navigate(`/casino/high-card-duel/${table._id}`)}
                                    className="bg-gradient-to-r from-gray-600 to-gray-500 hover:from-gray-500 hover:to-gray-400 text-white text-xs px-3 py-1 rounded-full transition-colors shadow-md shadow-gray-500/10"
                                  >
                                    View
                                  </button>
                                ) : (
                                  <span className="text-gray-500">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-gray-900/70 to-gray-800/70 border border-gray-700 rounded-xl p-6 text-center shadow-lg shadow-violet-500/5">
                  <p className="text-gray-400">No active tables at the moment. Be the first to create one!</p>
                </div>
              )}
            </div>
            
            {/* Game Rules */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">How to Play</h3>
                <button
                  onClick={() => setShowRules(!showRules)}
                  className="text-sm text-violet-400 hover:text-violet-300 transition-colors flex items-center px-3 py-1 rounded-lg bg-violet-500/10 border border-violet-500/20"
                >
                  {showRules ? 'Hide Rules' : 'Show Rules'}
                  <svg className={`w-4 h-4 ml-1 transform transition-transform ${showRules ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              
              {showRules && (
                <div className="bg-gradient-to-br from-gray-900/70 to-gray-800/70 border border-gray-700 rounded-xl p-6 shadow-lg shadow-violet-500/5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-lg font-medium mb-3 text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">Game Rules</h4>
                      <ul className="space-y-3 text-sm text-gray-300">
                        <li className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center mr-2 mt-0.5 border border-violet-500/30">
                            <svg className="w-3 h-3 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                            </svg>
                          </div>
                          Pay the entry fee to join a table.
                        </li>
                        <li className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center mr-2 mt-0.5 border border-violet-500/30">
                            <svg className="w-3 h-3 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                            </svg>
                          </div>
                          Once two players join, each gets one card.
                        </li>
                        <li className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center mr-2 mt-0.5 border border-violet-500/30">
                            <svg className="w-3 h-3 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                            </svg>
                          </div>
                          The player with the higher card wins.
                        </li>
                        <li className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center mr-2 mt-0.5 border border-violet-500/30">
                            <svg className="w-3 h-3 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                            </svg>
                          </div>
                          Winner gets 95% of the pot (5% platform fee).
                        </li>
                        <li className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center mr-2 mt-0.5 border border-violet-500/30">
                            <svg className="w-3 h-3 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                            </svg>
                          </div>
                          In case of a tie, entry fees are returned.
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-medium mb-3 text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-red-400">Card Ranks</h4>
                      <p className="text-sm text-gray-300 mb-3">
                        Cards rank from lowest to highest:
                      </p>
                      <div className="grid grid-cols-4 gap-1">
                        {['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'].map((value, index) => (
                          <div 
                            key={value} 
                            className={`bg-gradient-to-br ${
                              index >= 9 ? 'from-pink-900/40 to-pink-800/40 border-pink-700/30' : 'from-gray-800/60 to-gray-900/60 border-gray-700/50'
                            } p-2 rounded text-center border ${
                              index >= 9 ? 'text-pink-400' : 'text-gray-300'
                            } ${
                              index === 12 ? 'animate-pulse shadow-md shadow-pink-500/20' : ''
                            }`}
                          >
                            {value}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    )
  };

  // Main component render
  return (
    <div className="min-h-screen pb-12">    
      {view === 'lobby' ? renderLobbyView() : renderGameView()}
    </div>
  )
}

export default HighCardDuel;
